import type { LucideIcon } from 'lucide-react';
import {
  Heart,
  Star,
  Bookmark,
  Folder,
  Tag,
  Gift,
  ShoppingCart,
  Flame,
  Sparkles,
  Crown,
  Gem,
  Leaf,
  Sun,
  Moon,
  Music,
  Camera,
  Palette,
  Zap,
  Trophy,
  Clock,
} from 'lucide-react';

/**
 * Ported from ff-ui (`client/constants/collectionCustomization.ts`) so the admin
 * collection cards match the consumer app. The API stores color/icon *keys*.
 */

export const COLLECTION_COLOR_KEYS = [
  'rose',
  'peach',
  'amber',
  'lime',
  'mint',
  'teal',
  'sky',
  'indigo',
  'violet',
  'fuchsia',
  'slate',
  'stone',
] as const;

export type CollectionColorKey = (typeof COLLECTION_COLOR_KEYS)[number];

export const COLLECTION_COLOR_HEX: Record<CollectionColorKey, string> = {
  rose: '#FFE4E6',
  peach: '#FFECD2',
  amber: '#FEF3C7',
  lime: '#ECFCCB',
  mint: '#D1FAE5',
  teal: '#CCFBF1',
  sky: '#E0F2FE',
  indigo: '#E0E7FF',
  violet: '#EDE9FE',
  fuchsia: '#FAE8FF',
  slate: '#F1F5F9',
  stone: '#F5F5F4',
};

export const COLLECTION_ICON_KEYS = [
  'heart',
  'star',
  'bookmark',
  'folder',
  'tag',
  'gift',
  'cart',
  'fire',
  'sparkle',
  'crown',
  'gem',
  'leaf',
  'sun',
  'moon',
  'music',
  'camera',
  'palette',
  'bolt',
  'trophy',
  'clock',
] as const;

export type CollectionIconKey = (typeof COLLECTION_ICON_KEYS)[number];

export const COLLECTION_ICON_MAP: Record<CollectionIconKey, LucideIcon> = {
  heart: Heart,
  star: Star,
  bookmark: Bookmark,
  folder: Folder,
  tag: Tag,
  gift: Gift,
  cart: ShoppingCart,
  fire: Flame,
  sparkle: Sparkles,
  crown: Crown,
  gem: Gem,
  leaf: Leaf,
  sun: Sun,
  moon: Moon,
  music: Music,
  camera: Camera,
  palette: Palette,
  bolt: Zap,
  trophy: Trophy,
  clock: Clock,
};

export function isCollectionColorKey(v: string | null | undefined): v is CollectionColorKey {
  return typeof v === 'string' && (COLLECTION_COLOR_KEYS as readonly string[]).includes(v);
}

export function isCollectionIconKey(v: string | null | undefined): v is CollectionIconKey {
  return typeof v === 'string' && (COLLECTION_ICON_KEYS as readonly string[]).includes(v);
}

export function getCollectionHex(color: string | null | undefined): string | undefined {
  if (!color) return undefined;
  if (isCollectionColorKey(color)) return COLLECTION_COLOR_HEX[color];
  // Admin data may store a raw hex — pass it through.
  if (/^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(color)) return color;
  return undefined;
}

export function getCollectionIconComponent(icon: string | null | undefined): LucideIcon {
  if (icon && isCollectionIconKey(icon)) return COLLECTION_ICON_MAP[icon];
  return Folder;
}
